import { h } from "preact";
import {
  allowedFileTypes,
  calculateProgress,
  maxCountFile,
  maxSizeFile,
} from "../../helper";
import {
  formatSize,
  validateBigFile,
  validateDeniedType,
  validateEmptyFile,
} from "../../tools";
import { useEffect, useRef, useState } from "preact/compat";
import innowave from "../api/innowave";
import FileInputInnowaveProgress from "./FileInputInnowaveProgress";
import {
  uploadProgressBarInterval,
  uploadProgressBarSpeed,
} from "../constants/innowave";

/**
 *
 * @description added by Alexander G. 21.09.2022
 * @param file
 * @param index
 * @param onDeleteFile
 * @param field
 * @param onFileLoaded
 * @param onFileProcessed
 * @returns {JSX.Element}
 * @constructor
 */
export const FileListItem = ({
  file,
  index,
  field,
  onDeleteFile,
  onFileLoaded = () => {},
  onFileProcessed = () => {},
}) => {
  // error array as a state - because we want to update it
  const [errors, setErrors] = useState([]);

  // main state
  const [state, setState] = useState({
    progress: 0,
    completed: false,
    failed: false,
    batchId: null,
  });

  // we need ref to be able to access the up-to-date state within the callback (below)
  const stateRef = useRef(null);
  stateRef.current = state;

  const [definitions, setDefinitions] = useState([]);

  /**
   * @description checks current progress or fakes it if needed
   */
  const checkProgress = () => {
    // get up-to-date state from ref (above)
    const _state = stateRef.current;

    if (!_state.batchId) {
      // if batchId is missing, the file is probably uploading right now - so just fake it
      setState({
        ..._state,
        progress: calculateProgress(
          _state.progress,
          uploadProgressBarSpeed,
          uploadProgressBarInterval
        ),
      });
    } else {
      // fetch and save the current processing state
      innowave
        .getPercentProcessingBatch(_state.batchId)
        .then((response) => {
          if (response && !response.errorMessage && response.batchId) {
            return setState({
              ..._state,
              progress: calculateProgress(
                Math.max(
                  response.batchPercentProgressCompleted || 0,
                  _state.progress
                ),
                uploadProgressBarSpeed,
                uploadProgressBarInterval
              ),
              completed: !!response.isBatchProcessed,
            });
          }
          setState({ ..._state, failed: true });
          setErrors([
            ...errors,
            response.errorMessage ||
              "Произошла ошибка при проверке файла. Пожалуйста, проверьте файл и попробуйте ещё раз.",
          ]);
        })
        .catch((error) => {
          console.error(error);
          setState({ ..._state, failed: true });
        });
    }
  };

  /**
   * @description send file to Classificator API
   */
  const uploadFile = () => {
    innowave
      .uploadFile(file)
      .then((response) => {
        if (response && !response.errorMessage && response.batchId) {
          onFileLoaded(file, field)();
          return setState({
            ...state,
            progress: Math.max(stateRef.current.progress, 0),
            batchId: response.batchId,
          });
        }
        setState({ ...state, failed: true });
        setErrors([
          ...errors,
          response.errorMessage ||
            "Произошла ошибка при отправке файла на сервер. Попробуйте ещё раз, пожалуйста.",
        ]);
      })
      // most likely a network error or something, no need to show users anything, since it is useless for them
      .catch((error) => {
        console.error(error);
        setState({ ...state, failed: true });
      });
  };

  /**
   * @description fetches everything that needs to be fetched
   */
  const fetchDefinitions = () => {
    // fetch definitions and report to the controller class that we're done
    innowave
      .getFoundDefinitions(state.batchId)
      .then((response) => {
        if (response && !response.errorMessage && response.definitions) {
          setDefinitions(response.definitions);
          return onFileProcessed(file, field)(response.definitions);
        }
        setState({ ...state, failed: true });
        setErrors([
          ...errors,
          response.errorMessage ||
            "Произошла ошибка при проверке файла. Пожалуйста, проверьте файл и попробуйте ещё раз.",
        ]);
      })
      .catch((error) => {
        console.error(error);
        setState({ ...state, failed: true });
      });
  };

  // start effect - it is where the fun begins
  useEffect(() => {
    // process errors if
    let _errors = [];
    if (validateBigFile(maxSizeFile)(file))
      _errors.push("Ограничение на размер файлов " + formatSize(maxSizeFile));
    if (validateDeniedType(allowedFileTypes)(file))
      _errors.push("Допустимый формат файлов: " + allowedFileTypes.join(", "));
    if (validateEmptyFile(file)) _errors.push("Нельзя отправить пустой файл");
    if (index > maxCountFile - 1)
      _errors.push("Нельзя отправить более " + maxCountFile + " файлов");

    if (!_errors.length) {
      // ok, it's fine, let's upload our file
      uploadFile();

      // and also run check without delay
      checkProgress();
    }

    // set errors
    setErrors(_errors);
  }, []);

  // create interval to recheck the status
  const [checkInterval, setCheckInterval] = useState(null);
  useEffect(() => {
    // create interval for checking (only if nothing happened before)
    !checkInterval &&
      !errors.length &&
      !state.completed &&
      !state.failed &&
      setCheckInterval(
        setInterval(() => {
          checkProgress();
        }, uploadProgressBarInterval)
      );
  }, [state]);

  // check state and do stuff
  useEffect(() => {
    if (state.completed || state.failed) {
      // clear interval on success or failure - we don't need to check again and again
      clearInterval(checkInterval);
    }
  }, [state]);

  useEffect(() => {
    if (state.completed) {
      setTimeout(() => {
        // add a small lag to make it even more fun!
        // don't forget we're emulating, not actually displaying a real-time statistics
        fetchDefinitions();
      }, 1000);
    }
  }, [state.completed]);

  useEffect(() => {
    if (state.failed && !errors.length) {
      // show message
      setErrors([
        "Произошла ошибка при обработке файла. Пожалуйста, проверьте файлы и попробуйте ещё раз.",
      ]);
    }
  }, [state.failed]);

  // todo remove this
  useEffect(() => {
    definitions.length > 0 && console.debug(index, definitions);
  }, [definitions]);

  return (
    <li
      className={`file-input__files-item file-input__files-item--flex ${
        /* errors.length ? 'file-input__files-item_error' : '' */ null
      }`}
      key={file.name + file.size}
    >
      <div className="file-input__files-item__inner">
        {/* info*/}
        <span
          title={`${file.name} ${formatSize(file.size)}`}
          className="file-input__files-item__info"
        >
          <span className="file-input__files-item-name">{file.name}</span>
          <span className="file-input__files-item-size">
            {formatSize(file.size)}
          </span>
        </span>

        {/* progress bar */}
        {!state.failed && !errors.length && (
          <FileInputInnowaveProgress
            failed={state.failed}
            progress={state.progress}
          />
        )}

        {/* button */}
        <button
          className="file-input__files-item-delete"
          aria-label="Удалить файл"
          onClick={onDeleteFile(file, field)}
        />
      </div>

      {/* errors */}
      {errors.length > 0 && (
        <div className="file-input__files-item__errors">
          <span>
            {errors.map((errorItem, index) => (
              <p key={index} className="file-input__files-item-error-text">
                {errorItem}
              </p>
            ))}
          </span>
        </div>
      )}

      {/* note: remove 'false' if you want to display found definitions */}
      {false && definitions.length > 0 && (
        <div className="file-input__files-item__definitions">
          {definitions.map((item) => (
            <div className="file-input__files-item__definitions__item">
              <div className="file-input__files-item__definitions__item__inner">
                {item.definitionName}
              </div>

              <div className="file-input__files-item__definitions__item__inner">
                {item.pagesCount}
              </div>
            </div>
          ))}
        </div>
      )}
    </li>
  );
};

/**
 *
 * @description added by Alexander G. 21.09.2022
 * @param files
 * @param onDeleteFile
 * @param onFileLoaded
 * @param onFileProcessed
 * @constructor
 */
export const FileList = ({
  files,
  field,
  onDeleteFile,
  onFileLoaded = () => {},
  onFileProcessed = () => {},
}) => {
  return files.map((file, index) => (
    <FileListItem
      key={file.name + file.size}
      field={field}
      index={index}
      file={file}
      onDeleteFile={onDeleteFile}
      onFileLoaded={onFileLoaded}
      onFileProcessed={onFileProcessed}
    />
  ));
};

/**
 *
 * @param title
 * @param onChangeFile
 * @param errors
 * @param buttonNameNew
 * @param values
 * @param field
 * @param onDeleteFile
 * @param onFileLoaded
 * @param onFileProcessed
 * @returns {JSX.Element}
 * @constructor
 */
const FileInputInnowave = ({
  title,
  onChangeFile,
  errors,
  buttonNameNew,
  values,
  field,
  onDeleteFile,
  onFileLoaded = () => {},
  onFileProcessed = () => {},
}) => {
  const hasFiles = Array.isArray(values[field]) && values[field].length;
  // const fileList = hasFiles ? values[field].map((file, key) => renderFileRow(file, key, field, onDeleteFile)) : null;
  buttonNameNew = buttonNameNew || "Выбрать";

  return (
    <div className="form__item">
      <div
        className={`file-input ${
          field in errors ? "form-field_state_error" : ""
        }`}
      >
        <div className="file-input__title">{title}</div>
        <label className="file-input__body">
          <input
            className="file-input__input"
            multiple={true}
            type="file"
            onChange={onChangeFile(field)}
            value=""
          />
          <span className="file-input__button button-primary button-primary_pdv_lg">
            <span>{hasFiles ? "Добавить" : buttonNameNew}</span>
          </span>
          <span className="file-input__file-name">
            <span className="file-input__text">
              {hasFiles ? `Файлов выбрано: ${hasFiles}` : "Файлы не выбраны"}
            </span>
          </span>
        </label>
        {!!hasFiles && (
          <ul className="file-input__files-list">
            {
              <FileList
                files={values[field]}
                field={field}
                onDeleteFile={onDeleteFile}
                onFileLoaded={onFileLoaded}
                onFileProcessed={onFileProcessed}
              />
            }
          </ul>
        )}
      </div>
    </div>
  );
};

export default FileInputInnowave;
